import Enumerable from "linq";

import { ReceptionSearchActiveKindTranslator } from ".";

import { IWorkflowActionContentBean } from "../beans";
import { IWorkflowStatusNameBean, IFormatInfoBean, IWorkflowDestinationBean, IDateRangeBean, IWorkflowOperatorBean, IReceptionReportTypeBean, IReceptionInputSectionBean
} from "../beans/items";
import { IReceptionSearchConditionBean, IReceptionStatusSearchConditionBean, IReceptionDetailProgressSearchConditionBean, IReceptionTodoSearchConditionBean, IReceptionDetailStatusSearchConditionBean, IWorkflowStatusSearchConditionBean, IReceptionDetailSearchConditionBean, IReceptionDetailAllSearchConditionBean, IReceptionDetailPresetSortConditionBean, IReceptionDetailPresetSortConditionItemBean
} from "../beans/search";
import { IWorkflowDocumentBean } from "../beans/document";
import { IReceptionEditorBean, IWorkflowRouteEditorBean, IWorkflowPhaseEditorBean } from "../beans/editor";
import { IReceptionApplicantBean, IReceptionRecipientBean, IReceptionDeliveryBean, IReceptionContactBean, IReceptionContactDetailBean, IReceptionManagerBean, IReceptionProofPeriodBean, IReceptionCommentBean, IReceptionBean, IReceptionDetailBean, 
} from "../beans/reception";

import { IPatientBean } from "../../../patient/ver2/beans";
import { IUserBean } from "../../../user/ver2/beans";
import { IDepartmentBean } from "../../../department/ver2/beans";
import { ObjectTranslator } from "../../../document/ver2";

import { ReceptionDateTranslator, WorkflowActionContent } from "../../../../domain/workflow";
import { WorkflowDocument } from "../../../../domain/workflow/document";
import {
  ReceptionSearchCondition,
  ReceptionStatusSearchCondition,
  ReceptionDetailSearchCondition,
  ReceptionDetailProgressSearchCondition,
  ReceptionTodoSearchCondition,
  WorkflowStatusSearchCondition,
  ReceptionDetailStatusSearchCondition,
  ReceptionDetailPresetSearchCondition,
  ReceptionDetailPresetSortCondition,
  ReceptionDetailPresetSortConditionItem,
} from "../../../../domain/workflow/search";
import { ReceptionEditor, WorkflowRouteEditorModel, WorkflowPhaseEditorModel, ReceptionEditorInputUnitDispInfo } from "../../../../domain/workflow/editor";
import { ReceptionApplicant, ReceptionRecipient, ReceptionDelivery, ReceptionContact, ReceptionContactDetail, ReceptionManager, ReceptionProofPeriod, ReceptionComment, Reception, ReceptionDetail 
} from "../../../../domain/workflow/reception";
import { WorkflowStatusName, WorkflowDestination, WorkflowOperator, ReceptionReportType, ReceptionInputSection 
} from "../../../../domain/workflow/items";
import { ProgressConditionKinds, ReceptionDateKinds } from "../../../../domain/workflow/kinds";

import { Patient } from "../../../../domain/patient";
import { FormatReference } from "../../../../domain/format";
import { User } from "../../../../domain/user";
import { Department } from "../../../../domain/department";
import { DateDuration } from "../../../../domain/system";
import { ProjectTeam } from "../../../../domain/projectTeam";
import { IProjectTeamBean } from "../../../projectTeam/ver2/beans";
import { IReceptionDetailPresetSearchConditionBean } from "../beans/search/IReceptionDetailPresetSearchConditionBean";
import { IReceptionDetailPresetSearchConditionOrderBean } from "../beans/search/IReceptionDetailPresetSearchConditionOrderBean";

// 診断書WF用model--> bean変換クラス
export class ReceptionModelTranslator {

  private _dateTranslator: ReceptionDateTranslator = new ReceptionDateTranslator();
  private _documentTranslator: ObjectTranslator = new ObjectTranslator(this._getToken);

  constructor(private _getToken: () => string) {
  }

  // 依頼受付登録用Bean
  public toReceptionEditorBean(model: ReceptionEditor): IReceptionEditorBean {
    return {
      reception: this.toReceptionBean(model.reception),
      receptionDetails: this.toReceptionDetailBeans(model.receptionDetails, model.displayInfo.inputUnitSetting, model.commonReceptionDetail),
      requestSheetFormats: null, // 登録用には不要
      displaySetting: null, // 登録用には不要
      patientAddress: null, // 登録用には不要
      hisInformation: null, // 登録用には不要
      sourceReceptionId: model.sourceReceptionId,
      sourceReceptionDetailId: model.sourceReceptionDetailId,
      lastWorkflowHistoryId: model.lastWorkflowHistoryId,
    };
  }

  // 申請者
  private toReceptionApplicantBean(model: ReceptionApplicant): IReceptionApplicantBean {
    return {
      name: model.name,
      relationship: model.relationship,
      phoneNumber: model.phoneNumber,
      zipCode: model.zipCode,
      address: model.address,
      cellPhone: model.cellPhone,
      email: model.email,
      isPatientAddress: model.isPatientAddress,
    };
  }

  // 受取者
  private toReceptionRecipientBean(model: ReceptionRecipient): IReceptionRecipientBean {
    return {
      receiptDate: this._dateTranslator.toDateString(model.receiptDate),
      receiptTime: this._dateTranslator.toTimeString(model.receiptTime),
      name: model.name,
      relationship: model.relationship,
      paymentStatus: model.paymentStatus,
      receiptMoney: model.receiptMoney,
      note: model.note
    };
  }

  // 交付先
  private toReceptionDeliveryBean(model: ReceptionDelivery): IReceptionDeliveryBean {
    return {
      type: model.type,
      generalFlag1: model.generalFlag1,
      zipCode: model.zipCode,
      address: model.address,
      note: model.note
    };
  }

  // 連絡先
  private toReceptionContactBean(model: ReceptionContact): IReceptionContactBean {
    return {
      details: this.toReceptionContactDetailsBean(model.details),
      note: model.note,
    };
  }

  private toReceptionContactDetailsBean(details: ReceptionContactDetail[]): IReceptionContactDetailBean[] {
    return Enumerable.from(details).select(d => this.toReceptionContactDetailBean(d)).toArray();
  }

  private toReceptionContactDetailBean(detail: ReceptionContactDetail): IReceptionContactDetailBean {
    return {
      isRequired: detail.isRequired,
      contacts: detail.contacts,
      contactName: detail.contactName,
    };
  }

  // 対応責任者
  private toReceptionManagerBean(model: ReceptionManager): IReceptionManagerBean {
    if (!model) { return null; }
    return {
      displayOrder: model.displayOrder,
      staff: this.toUserBean(model.staff)
    };
  }

  public toReceptionManagerBeans(models: ReceptionManager[]): IReceptionManagerBean[] {
    return Enumerable.from(models)
      .select(model => this.toReceptionManagerBean(model))
      .toArray();
  }

  // 証明期間
  private toReceptionProofPeriodBean(model: ReceptionProofPeriod): IReceptionProofPeriodBean {
    return {
      displayOrder: model.displayOrder,
      type: model.type,
      startDate: this._dateTranslator.toDateString(model.period.from),
      endDate: this._dateTranslator.toDateString(model.period.to)
    };
  }

  private toReceptionProofPeriodBeans(models: ReceptionProofPeriod[]): IReceptionProofPeriodBean[] {
    return Enumerable.from(models)
      .select(model => this.toReceptionProofPeriodBean(model))
      .toArray();
  }

  // コメント
  public toReceptionCommentBean(model: ReceptionComment): IReceptionCommentBean {
    if (!model) { return null; }
    return {
      id: model.id,
      archiveId: model.archiveId, // 登録競合チェックで使用
      type: model.type,
      content: model.content,
      createDateTime: this._dateTranslator.toDateTimeString(model.createDateTime),
      createUser: this.toUserBean(model.createUser),
      deleted: model.deleted,
      isEditable: false, // サーバに送信しても使われないので
      isDeletable: false, // サーバに送信しても使われないので
      updateDateTime: this._dateTranslator.toDateTimeString(model.updateDateTime),
    };
  }

  public toEditReceptionCommentBean(model: ReceptionComment): IReceptionCommentBean {
    // 作成者が無い時はコメントが新規なので、コメント内容が空の時は登録しない
    if (!model
      || (!model.createUser && !model.content)) { return null; }
    
    return this.toReceptionCommentBean(model);
  }

  // private toReceptionCommentBeans(models: ReceptionComment[]): IReceptionCommentBean[] {
  //   return Enumerable.from(models)
  //     .select(model => this.toReceptionCommentBean(model))
  //     .toArray();
  // }

  // WFルート設定
  private toWorkflowRouteEditorBean(model: WorkflowRouteEditorModel, phases: WorkflowPhaseEditorModel[], progressCondition: ProgressConditionKinds): IWorkflowRouteEditorBean {
    if (!model) { return null; }
    return {
      progressCondition: progressCondition,
      phases: this.toWorkflowPhaseEditorBeans(phases),
      isPaper: model.isPaper,
      id: null,
      name: model.name,
    };
  }

  // WFフェーズ設定
  private toWorkflowPhaseEditorBeans(models: WorkflowPhaseEditorModel[]): IWorkflowPhaseEditorBean[] {
    return Enumerable.from(models)
      .select(model => this.toWorkflowPhaseEditorBean(model))
      .toArray();
  }

  private toWorkflowPhaseEditorBean(model: WorkflowPhaseEditorModel): IWorkflowPhaseEditorBean {
    if (!model) { return null; }
    return {
      correspondStatus: this.toWorkflowStatusNameBean(model.correspondStatus),
      limitDays: model.limitDays,
      limitDate: this._dateTranslator.toDateString(model.limitDate),
      isEnable: model.isEnable,
      phaseStatus: null, // サーバに送信する必要は無い
    };
  }

  // WF状態名
  private toWorkflowStatusNameBean(model: WorkflowStatusName): IWorkflowStatusNameBean {
    if (!model) { return null; }
    return {
      code: model.code,
      name: model.name,
      waitName: model.waitName,
      doneName: model.doneName,
      type: model.type,
    };
  }

  // 依頼受付
  private toReceptionBean(model: Reception): IReceptionBean {
    return {
      id: model.id,
      patient: this.toPatientBean(model.patientInfo.patient),
      receptionNumber: model.receptionNumber,
      receptionDate: this._dateTranslator.toDateString(model.receptionDate),
      inputSection: model.inputSection ? { code: model.inputSection.id, name: model.inputSection.name, isDisable: model.inputSection.isDisable } : null,
      documentId: null, // documentId: model.document.id,
      isTemporary: model.isTemporary,
      isMigrated: model.isMigrated,
      applicant: this.toReceptionApplicantBean(model.applicant),
      recipient: this.toReceptionRecipientBean(model.recipient),
      delivery: this.toReceptionDeliveryBean(model.delivery),
      contact: this.toReceptionContactBean(model.contact),
      deleted: model.deleted,
      patientNote: model.patientInfo.patientNote,
      isPatientNoteUpdated: model.patientInfo.patientNote !== model.patientInfo.patientNoteBeforeEditing,
      inOutType: model.inOutType,
    };
  }

  // 依頼詳細
  private toReceptionDetailBeans(models: ReceptionDetail[], inputUnitSetting: ReceptionEditorInputUnitDispInfo, commonModel: ReceptionDetail): IReceptionDetailBean[] {
    return Enumerable.from(models)
      .where(model => !model.isEmpty) // 空のものは間引く
      .select(model => this.toReceptionDetailBean(model, inputUnitSetting, commonModel))
      .toArray();
  }

  private toReceptionDetailBean(model: ReceptionDetail, inputUnitSetting: ReceptionEditorInputUnitDispInfo, commonModel: ReceptionDetail): IReceptionDetailBean {
    return {
      id: model.id,
      format: this.toFormatObjectBean(model.format),
      formatStatus: model.formatStatus,
      isPaper: model.isPaper,
      paperName: model.isPaper ? model.paperName : "",
      paperCompanyName: model.isPaper ? model.paperCompanyName : "",
      department: this.toDepartmentBean((inputUnitSetting.isDepartmentInputSingle ? commonModel : model).department),
      workflowId: model.workflowId,
      documentId: null, // model.document.id,
      sourceReceptionDetailId: model.sourceReceptionDetailId,
      sourceDocumentId: model.sourceDocumentId,
      isSuspended: null,
      suspendUser: null,
      managers: this.toReceptionManagerBeans((inputUnitSetting.isManagerInputSingle ? commonModel : model).managers),
      disease: model.disease,
      proofPeriods: this.toReceptionProofPeriodBeans(model.proofPeriods),
      commentSet: null,
      route: this.toWorkflowRouteEditorBean(
        model.route, 
        (inputUnitSetting.isPhaseInputSingle ? commonModel : model).route.phases,
        (inputUnitSetting.isManagerInputSingle ? commonModel : model).route.progressCondition
      ),
      documentFixPhaseStatus: model.documentFixPhaseStatus,
      documentFixPahseProcessedManagers: null,
      comment: this.toReceptionCommentBean((inputUnitSetting.isCommentInputSingle ? commonModel : model).comment),
      restrictComment: this.toReceptionCommentBean(model.restrictComment),
      nextMessage: this.toReceptionCommentBean((inputUnitSetting.isNextMessageInputSingle ? commonModel : model).nextMessage),
      editComment: this.toEditReceptionCommentBean(model.editComment),
      editRestrictComment: this.toEditReceptionCommentBean(model.editRestrictComment),
      editNextMessage: this.toEditReceptionCommentBean(model.editNextMessage),
      deleted: model.deleted,
      projectTeam: this.toProjectTeamBean(model.projectTeam),
    };
  }

  // WF文書
  public toWorkflowDocumentBean(model: WorkflowDocument): IWorkflowDocumentBean {
    return {
      targetId: model.targetId,
      document: this._documentTranslator.toSaveDocumentBean(model.document),
      permissions: null, // サーバに送信する必要は無い
      status: null, // サーバに送信する必要は無い
      nextMessageCommentId: model.nextMessageCommentId, // 登録競合チェックで使用
      nextMessageArchiveId: model.nextMessageArchiveId, // 登録競合チェックで使用
      nextMessage: model.nextMessage,
      workflowHisInformation: null, // サーバに送信する必要は無い
      lastWorkflowHistoryId: model.lastWorkflowHistoryId,
      isAutoRegister: model.isAutoRegister,
      createToken: model.createTokens ? model.createTokens.token : null,
      isWriteAndPublish: model.isWriteAndPublish,
      isRegisteredWithPublish: model.isRegisteredWithPublish,
    };
  }

  // WF実行内容
  public toWorkflowActionContentBean(model: WorkflowActionContent): IWorkflowActionContentBean {
    return {
      triggerCode: model.triggerCode,
      nextMessageCommentId: model.nextMessageCommentId, // 登録競合チェックで使用
      nextMessageArchiveId: model.nextMessageArchiveId, // 登録競合チェックで使用
      nextMessage: model.nextMessage,
      destination: this.toWorkflowDestinationBean(model.destination),
      backMessage: model.backMessage,
      lastWorkflowHistoryId: model.lastWorkflowHistoryId,
    };
  }

  // WF行き先
  private toWorkflowDestinationBean(model: WorkflowDestination): IWorkflowDestinationBean {
    if (!model) { return null; }

    return {
      statusName: model.statusName,
      phaseId: model.phaseId,
      actionId: model.actionId,
      actor: this.toUserBean(model.actor),
      operationTime: this._dateTranslator.toDateTimeString(model.operationTime),
      operator: this.toWorkflowOperatorBean(model.operator),
    };
  }

  // WF実施者
  private toWorkflowOperatorBean(model: WorkflowOperator): IWorkflowOperatorBean {
    if (!model) { return null; }

    return {
      user: this.toUserBean(model.user),
      professionName: model.professionName,
    };
  }

  // 依頼受付検索条件
  public toReceptionSearchConditionBean(model: ReceptionSearchCondition): IReceptionSearchConditionBean {
    return {
      receptionDate: model.dateKind === ReceptionDateKinds.ReceptionDate ? this.toDateRangeBean(model.duration) : null,
      createDate: model.dateKind === ReceptionDateKinds.CreateDate ? this.toDateRangeBean(model.duration) : null,
      receiptDate: model.dateKind === ReceptionDateKinds.ReceiptDate ? this.toDateRangeBean(model.duration) : null,
      patient: this.toPatientBean(model.patient),
      formats: this.toFormatOjectBeans(model.formats),
      paperName: model.paperName,
      managers: this.toUserBeans(model.managers),
      creators: this.toUserBeans(model.creators),
      departments: this.toDepartmentBeans(model.departments),
      receptionNumber: model.receptionNumber,
      deliveryType: model.deliveryType.code,
      generalFlag1: ReceptionSearchActiveKindTranslator.toBoolean(model.generalFlag1Status),
      inOutType: model.inOutType,
      statuses: this.toReceptionStatusSearchConditionBean(model.statuses),
    };
  }

  private toReceptionStatusSearchConditionBean(model: ReceptionStatusSearchCondition): IReceptionStatusSearchConditionBean {
    return {
      isTemporary: model.isTemporary,
      isProcessing: model.isProcessing,
      isFinished: model.isFinished,
      isDeleted: model.isDeleted,
    };
  }

  // 依頼詳細検索条件
  public toReceptionDetailSearchConditionBean(model: ReceptionDetailSearchCondition): IReceptionDetailSearchConditionBean {
    return {
      receptionDate: model.dateKind === ReceptionDateKinds.ReceptionDate ? this.toDateRangeBean(model.duration) : null,
      createDate: model.dateKind === ReceptionDateKinds.CreateDate ? this.toDateRangeBean(model.duration) : null,
      updateDate: model.dateKind === ReceptionDateKinds.UpdateDate ? this.toDateRangeBean(model.duration) : null,
      finishDate: model.dateKind === ReceptionDateKinds.FinishDate ? this.toDateRangeBean(model.duration) : null,
      limitDate: this.toDateRangeBean(model.limitDate),
      patient: this.toPatientBean(model.patient),
      isPaper: ReceptionSearchActiveKindTranslator.toBoolean(model.paperStatus),
      reportType: this.toReceptionReportTypeBean(model.reportType),
      formats: this.toFormatOjectBeans(model.formats),
      paperName: model.paperName,
      managers: this.toUserBeans(model.managers),
      creators: this.toUserBeans(model.creators),
      departments: this.toDepartmentBeans(model.departments),
      receptionNumber: model.receptionNumber,
      // 他医依頼有無
      deliveryType: model.deliveryType.code,
      generalFlag1: ReceptionSearchActiveKindTranslator.toBoolean(model.generalFlag1Status),
      statuses: this.toReceptionDetailStatusSearchConditionBean(model.statuses),
      workflowStatuses: this.toWorkflowStatusSearchConditionBeans(model.workflowStatuses),
      inputSection: this.toReceptionInputSectionBean(model.inputSection),
      inOutType: model.inOutType,
    };
  }

  // 依頼詳細検索（WF進捗別）条件
  public toReceptionDetailProgressSearchConditionBean(model: ReceptionDetailProgressSearchCondition): IReceptionDetailProgressSearchConditionBean {
    return {
      receptionDate: model.dateKind === ReceptionDateKinds.ReceptionDate ? this.toDateRangeBean(model.duration) : null,
      createDate: model.dateKind === ReceptionDateKinds.CreateDate ? this.toDateRangeBean(model.duration) : null,
      updateDate: model.dateKind === ReceptionDateKinds.UpdateDate ? this.toDateRangeBean(model.duration) : null,
      finishDate: model.dateKind === ReceptionDateKinds.FinishDate ? this.toDateRangeBean(model.duration) : null,
      deliveryLimitDate: this.toDateRangeBean(model.deliveryLimitDate),
      limitDate: this.toDateRangeBean(model.limitDate),
      patient: this.toPatientBean(model.patient),
      formats: this.toFormatOjectBeans(model.formats),
      paperName: model.paperName,
      managers: this.toUserBeans(model.managers),
      departments: this.toDepartmentBeans(model.departments),
      generalFlag1: ReceptionSearchActiveKindTranslator.toBoolean(model.generalFlag1Status),
      statuses: this.toReceptionDetailStatusSearchConditionBean(model.statuses),
      workflowStatus: this.toWorkflowStatusSearchConditionBean(model.workflowStatus),
      inputSection: this.toReceptionInputSectionBean(model.inputSection),
      inOutType: model.inOutType,
      isMyselfOnly: model.isMyselfOnly,
    };
  }
  
  // 依頼詳細検索（プリセット）条件
  public toReceptionDetailAllSearchConditionBean(model: ReceptionDetailSearchCondition): IReceptionDetailAllSearchConditionBean {
    return {
      duration: this.toDateRangeBean(model.duration),
      durationPickerKey: model.durationPickerKey,
      dateKind: model.dateKind,
      limitDate: this.toDateRangeBean(model.limitDate),
      limitDatePickerKey: model.limitDatePickerKey,
      patient: this.toPatientBean(model.patient),
      isPaper: ReceptionSearchActiveKindTranslator.toBoolean(model.paperStatus),
      reportType: this.toReceptionReportTypeBean(model.reportType),
      formats: this.toFormatOjectBeans(model.formats),
      paperName: model.paperName,
      managers: this.toUserBeans(model.managers),
      isManagerMyself: model.isManagerMyself,
      creators: this.toUserBeans(model.creators),
      isCreatorMyself: model.isCreatorMyself,
      departments: this.toDepartmentBeans(model.departments),
      receptionNumber: model.receptionNumber,
      // 他医依頼有無
      deliveryType: model.deliveryType.code,
      generalFlag1: ReceptionSearchActiveKindTranslator.toBoolean(model.generalFlag1Status),
      statuses: this.toReceptionDetailStatusSearchConditionBean(model.statuses),
      workflowStatuses: this.toWorkflowStatusSearchConditionBeans(model.workflowStatuses),
      inputSection: this.toReceptionInputSectionBean(model.inputSection),
      inOutType: model.inOutType,
      // 患者、診療科指定
      isPatientIdSpecified: model.isPatientIdSpecified,
      isDepartmentIdSpecified: model.isDepartmentIdSpecified,
      presetPatient: this.toPatientBean(model.presetPatient),
      presetDepartments: this.toDepartmentBeans(model.presetDepartments),
    };
  }

  // TODO検索条件
  public toReceptionTodoSearchConditionBean(model: ReceptionTodoSearchCondition): IReceptionTodoSearchConditionBean {

    const wfStatus = new WorkflowStatusSearchCondition();
    wfStatus.isSelected = true;
    wfStatus.status = model.workflowStatus;

    return {
      receptionDate: model.dateKind === ReceptionDateKinds.ReceptionDate ? this.toDateRangeBean(model.duration) : null,
      isPaper: ReceptionSearchActiveKindTranslator.toBoolean(model.paperStatus),
      reportType: this.toReceptionReportTypeBean(model.reportType),
      patient: this.toPatientBean(model.patient),
      generalFlag1: ReceptionSearchActiveKindTranslator.toBoolean(model.generalFlag1Status),
      department: this.toDepartmentBean(model.department),
      statuses: this.toReceptionDetailStatusSearchConditionBean(model.statuses),
      workflowStatus: model.workflowStatus ? this.toWorkflowStatusSearchConditionBean(wfStatus) : null,
    };
  }

  private toReceptionDetailStatusSearchConditionBean(model: ReceptionDetailStatusSearchCondition): IReceptionDetailStatusSearchConditionBean {
    return {
      isExpired: model.isExpired,
      isTemporary: model.isTemporary,
      isBacked: model.isBacked,
    };
  }

  // WF状態検索条件
  private toWorkflowStatusSearchConditionBean(model: WorkflowStatusSearchCondition): IWorkflowStatusSearchConditionBean {
    if (!model) { return null; }
    return {
      isSelected: model.isSelected,
      status: this.toWorkflowStatusNameBean(model.status),
    };
  }

  // 検索プリセット条件
  public toPresetConditionBean(model: ReceptionDetailPresetSearchCondition): IReceptionDetailPresetSearchConditionBean {
    const condition = this.toReceptionDetailAllSearchConditionBean(model.condition);
    // 日付関連の条件は保存しないので削除
    condition.duration = null;
    condition.limitDate = null;
    return {
      key: model.key,
      name: model.name,
      condition: condition,
      presetKind: model.presetKind,
      index: model.index,
      departmentId: model.departmentId,
      isDefaultSelected: model.isDefaultSelected,
      isStatusTabEnabled: model.isStatusTabEnabled
    };
  } 

  // 検索プリセット条件の並び順
  public toPresetSearchConditionOrderBeans(model: string[]): IReceptionDetailPresetSearchConditionOrderBean[] {
    return Enumerable.from(model).select(m => this.toPresetSearchConditionOrderBean(m)).toArray();
  }

  public toPresetSearchConditionOrderBean(model: string): IReceptionDetailPresetSearchConditionOrderBean {
    return {
      key: model,
    };
  }

  // ソート条件プリセット
  public toPresetSortConditionBeans(models: ReceptionDetailPresetSortCondition[]): IReceptionDetailPresetSortConditionBean[] {
    return Enumerable.from(models).select(model => this.toPresetSortConditionBean(model)).toArray();
  } 

  private toPresetSortConditionBean(model: ReceptionDetailPresetSortCondition): IReceptionDetailPresetSortConditionBean {
    return {
      label: model.label,
      items: this.toPresetSortConditionItemBeans(model.items),
      isDefault: model.isDefault,
    };
  } 

  private toPresetSortConditionItemBeans(models: ReceptionDetailPresetSortConditionItem[]): IReceptionDetailPresetSortConditionItemBean[] {
    return Enumerable.from(models).select(model => this.toPresetSortConditionItemBean(model)).toArray();
  } 

  private toPresetSortConditionItemBean(model: ReceptionDetailPresetSortConditionItem): IReceptionDetailPresetSortConditionItemBean {
    return {
      sortType: model.sortType,
      orderType: model.orderType,
    };
  } 

  private toWorkflowStatusSearchConditionBeans(models: WorkflowStatusSearchCondition[]): IWorkflowStatusSearchConditionBean[] {
    return Enumerable.from(models)
    .select(model => this.toWorkflowStatusSearchConditionBean(model))
    .toArray();
  }

  private toReceptionInputSectionBean(model: ReceptionInputSection): IReceptionInputSectionBean {
    if (!model || model.id === null) { return null; }
    return {
      code: model.id,
      name: model.name,
      isDisable: model.isDisable,
    };
  }

  // 書類種別
  private toReceptionReportTypeBean(model: ReceptionReportType): IReceptionReportTypeBean {
    if (!model) { return null; }
    return {
      id:  model.id,
      name: model.name,
    };
  }

  private toDateRangeBean(model: DateDuration): IDateRangeBean {
    if (!model) { return null; }
    return {
      from: this._dateTranslator.toDateString(model.from),
      to: this._dateTranslator.toDateString(model.to),
    };
  }


  // // フォーマット TODO：共通化
  // private toFormatBean(model: FormatReference): IFormatBean {
  //   if (!model) { return null; }
  //   return {
  //     id: model.id,
  //     name: model.name
  //   };
  // }

  // フォーマット TODO：共通化
  private toFormatObjectBean(model: FormatReference): IFormatInfoBean {
    if (!model) { return null; }

    return {
      originalId: model.id,
      formatName: model.name,
      companyCode: model.company ? model.company.id : null,
      companyName: model.company ? model.company.name : null,
      reportTypeId: model.reportTypeId,
      categoryType: model.categoryType,
      writeMethodType: model.writeMethodType,
    };
  }

  private toFormatOjectBeans(models: FormatReference[]): IFormatInfoBean[] {
    return Enumerable.from(models)
    .select(model => this.toFormatObjectBean(model))
    .toArray();
  }

  // ユーザ TODO：共通化
  private toUserBean(model: User): IUserBean {
    if (!model) { return null; }
    return {
      code: model.id,
      name: model.name,
      profession: model.profession ? model.profession.id : null,
      professionName: model.profession ? model.profession.name : null,
      kanaName: model.kanaName,
      email: model.email,
      phone: model.phone,
    };
  }

  private toUserBeans(models: User[]): IUserBean[] {
    return Enumerable.from(models)
    .select(model => this.toUserBean(model))
    .toArray();
  }

  // 診療科 TODO：共通化
  private toDepartmentBean(model: Department): IDepartmentBean {
    if (!model) { return null; }
    return {
      departmentId: model.id,
      name: model.name,
      shortName: model.shortName,
      isDeleted: model.isDeleted,
    };
  }

  private toDepartmentBeans(models: Department[]): IDepartmentBean[] {
    return Enumerable.from(models)
      .select(model => this.toDepartmentBean(model))
      .toArray();
  }

  // 患者 TODO:共通化
  private toPatientBean(model: Patient): IPatientBean {
    if (!model) { return null; }
    return {
      patientId: model.id,
      name: model.name,
      kanaName: model.kanaName,
      sex: model.sex,
      birthDate: this._dateTranslator.toDateString(model.birthday),
    };
  }

  // プロジェクトチーム TODO:共通化
  private toProjectTeamBean(model: ProjectTeam): IProjectTeamBean {
    if (!model || model.id == null) { return null; }
    return {
      id: model.id,
      name: model.name,
      kanaName: model.kanaName,
      department: null,
      members: null,
      isDeleted: false,
    };
  }
}
